import { useState, useCallback, useRef, useEffect } from "react";

/**
 * Interface for structured syntax error information
 */
export interface SyntaxError {
  message: string;
  severity: "error" | "warning";
  /** Line number (1-indexed) if available */
  line?: number;
  /** Column number (1-indexed) if available */
  column?: number;
  /** Whether this is a "trailing partial" error from incomplete code */
  isTrailingPartial: boolean;
}

/**
 * Result of syntax validation
 */
export interface SyntaxValidationResult {
  /** Whether the content is syntactically valid */
  isValid: boolean;
  /** Array of errors found during validation */
  errors: SyntaxError[];
  /** Content up to the first non-trailing error (for correction flow) */
  validPrefix: string;
  /** The content that was validated */
  content: string;
  /** Timestamp of validation */
  timestamp: number;
}

export interface UseSyntaxValidatorOptions {
  /** Debounce interval in milliseconds (default: 250ms) */
  debounceMs?: number;
  /** Whether validation is enabled (default: true) */
  enabled?: boolean;
  /** Callback when a non-trailing error is detected - can trigger interruption */
  onCriticalError?: (result: SyntaxValidationResult) => void;
}

interface UseSyntaxValidatorReturn {
  /** Whether the WASM module is ready for validation */
  isReady: boolean;
  /** Current validation result (null if not yet validated) */
  validationResult: SyntaxValidationResult | null;
  /** Whether validation is currently in progress */
  isValidating: boolean;
  /** Manually trigger validation for given content */
  validate: (content: string) => Promise<SyntaxValidationResult>;
  /** Reset validation state */
  reset: () => void;
}

/**
 * Patterns that indicate "trailing partial" syntax - incomplete code that is still being typed.
 * These are NOT real errors and should be ignored during streaming.
 */
const TRAILING_PARTIAL_PATTERNS = [
  // Unclosed brackets at end
  /[({[]$/,
  // Incomplete function call: `#func(` or `#func(`
  /#\w+\s*\($/,
  // Incomplete content block: `[text` or `[ text
  /\[\s*[^\]]*$/,
  // Unclosed string at end
  /"[^"]*$/,
  // Incomplete hash command: just `#` or `#word`
  /#\w*$/,
  // Incomplete set/show rule
  /#(set|show)\s*$/,
  // Incomplete let binding
  /#let\s+\w*\s*=?\s*$/,
  // Incomplete grid/table row
  /\[[^\]]*,\s*$/,
];

/**
 * Error messages from the Typst compiler that typically indicate incomplete code
 */
const INCOMPLETE_CODE_MESSAGES = [
  "expected closing paren",
  "expected closing bracket",
  "expected closing brace",
  "unclosed delimiter",
  "expected expression",
  "expected identifier",
  "expected block",
  "expected content",
  "unterminated string",
];

/**
 * Hook for real-time Typst syntax validation during streaming.
 * Uses the same WASM module as useTypstCompiler but runs validation
 * incrementally with debouncing.
 *
 * @param options Configuration options
 * @returns Validation state and functions
 *
 * @example
 * ```tsx
 * const { isReady, validationResult, validate } = useSyntaxValidator({
 *   debounceMs: 250,
 *   onCriticalError: (result) => {
 *     // Trigger stream interruption
 *     handleStreamInterruption(result.validPrefix, result.errors[0].message);
 *   }
 * });
 *
 * // During streaming
 * useEffect(() => {
 *   if (isStreaming && content) {
 *     validate(content);
 *   }
 * }, [content, isStreaming]);
 * ```
 */
export function useSyntaxValidator(
  options: UseSyntaxValidatorOptions = {}
): UseSyntaxValidatorReturn {
  const { debounceMs = 250, enabled = true, onCriticalError } = options;

  const [isReady, setIsReady] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<SyntaxValidationResult | null>(null);

  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastValidatedContentRef = useRef<string>("");
  const onCriticalErrorRef = useRef(onCriticalError);

  // Keep ref updated
  useEffect(() => {
    onCriticalErrorRef.current = onCriticalError;
  }, [onCriticalError]);

  // Check for WASM module readiness
  useEffect(() => {
    const checkReady = () => {
      if (typeof window !== "undefined" && window.$typst) {
        console.log("[useSyntaxValidator] ✅ WASM module ready");
        setIsReady(true);
        return true;
      }
      return false;
    };

    if (checkReady()) return;

    // Poll for module
    const interval = setInterval(() => {
      if (checkReady()) {
        clearInterval(interval);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(interval);
      if (!window.$typst) {
        console.error("[useSyntaxValidator] ❌ WASM module not available after 10s");
      }
    }, 10000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  /**
   * Determine if an error is a "trailing partial" that should be ignored
   */
  const isTrailingPartialError = useCallback((content: string, errorMessage: string): boolean => {
    // Check if content ends with incomplete patterns
    const trimmedContent = content.trimEnd();
    for (const pattern of TRAILING_PARTIAL_PATTERNS) {
      if (pattern.test(trimmedContent)) {
        console.log("[useSyntaxValidator] Detected trailing partial pattern:", pattern.toString());
        return true;
      }
    }

    // Check if error message indicates incomplete code
    const lowerMessage = errorMessage.toLowerCase();
    for (const incomplete of INCOMPLETE_CODE_MESSAGES) {
      if (lowerMessage.includes(incomplete)) {
        // Only consider it trailing partial if the error is near the end
        // TODO: Use line/column info from SourceDiagnostic for accuracy
        console.log("[useSyntaxValidator] Detected incomplete code message:", incomplete);
        return true;
      }
    }

    return false;
  }, []);

  /**
   * Extract valid prefix from content up to the error point
   */
  const extractValidPrefix = useCallback((content: string, errorLine?: number): string => {
    if (!errorLine || errorLine <= 1) {
      // If no line info or error on first line, can't extract prefix
      return "";
    }

    const lines = content.split("\n");
    // Return content up to (but not including) the error line
    return lines.slice(0, errorLine - 1).join("\n");
  }, []);

  /**
   * Parse SourceDiagnostic from Typst WASM compiler
   */
  const parseDiagnostics = useCallback(
    (errors: unknown[], content: string): SyntaxError[] => {
      if (!Array.isArray(errors)) return [];

      return errors.map((err: any) => {
        const message = err.message || String(err);
        const line = err.span?.start?.line;
        const column = err.span?.start?.column;
        const severity = err.severity === "warning" ? "warning" : "error";

        return {
          message,
          severity,
          line,
          column,
          isTrailingPartial: isTrailingPartialError(content, message),
        };
      });
    },
    [isTrailingPartialError]
  );

  /**
   * Core validation function - attempts to compile and catches errors
   */
  const validateContent = useCallback(
    async (content: string): Promise<SyntaxValidationResult> => {
      if (!window.$typst) {
        return {
          isValid: true, // Assume valid if can't check
          errors: [],
          validPrefix: content,
          content,
          timestamp: Date.now(),
        };
      }

      try {
        // Attempt to compile - if it succeeds, content is valid
        await window.$typst.pdf({ mainContent: content });

        console.log("[useSyntaxValidator] ✅ Content validated successfully");
        return {
          isValid: true,
          errors: [],
          validPrefix: content,
          content,
          timestamp: Date.now(),
        };
      } catch (err) {
        // Parse the errors from the compiler
        const rawErrors = Array.isArray(err) ? err : [err];
        const errors = parseDiagnostics(rawErrors, content);

        // Filter out trailing partial errors
        const criticalErrors = errors.filter((e) => !e.isTrailingPartial && e.severity === "error");
        const hasOnlyCriticalErrors = criticalErrors.length > 0;

        // If all errors are trailing partials, consider it "valid for now"
        if (!hasOnlyCriticalErrors) {
          console.log(
            "[useSyntaxValidator] ⚠️ Only trailing partial errors, content is OK for now"
          );
          return {
            isValid: true,
            errors,
            validPrefix: content,
            content,
            timestamp: Date.now(),
          };
        }

        // We have real errors - extract valid prefix
        const firstError = criticalErrors[0];
        const validPrefix = extractValidPrefix(content, firstError.line);

        console.log("[useSyntaxValidator] ❌ Critical syntax error detected:", firstError.message);
        console.log("[useSyntaxValidator] Valid prefix length:", validPrefix.length);

        const result: SyntaxValidationResult = {
          isValid: false,
          errors,
          validPrefix,
          content,
          timestamp: Date.now(),
        };

        // Trigger callback for interruption handling
        if (onCriticalErrorRef.current) {
          onCriticalErrorRef.current(result);
        }

        return result;
      }
    },
    [parseDiagnostics, extractValidPrefix]
  );

  /**
   * Debounced validation function for streaming content
   */
  const validate = useCallback(
    async (content: string): Promise<SyntaxValidationResult> => {
      if (!enabled || !content) {
        return {
          isValid: true,
          errors: [],
          validPrefix: content,
          content,
          timestamp: Date.now(),
        };
      }

      // Skip if content unchanged
      if (content === lastValidatedContentRef.current && validationResult) {
        return validationResult;
      }

      return new Promise((resolve) => {
        // Clear existing debounce timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        debounceTimerRef.current = setTimeout(async () => {
          setIsValidating(true);
          console.log(`[useSyntaxValidator] Validating content (${content.length} chars)...`);

          const result = await validateContent(content);
          lastValidatedContentRef.current = content;
          setValidationResult(result);
          setIsValidating(false);

          resolve(result);
        }, debounceMs);
      });
    },
    [enabled, debounceMs, validateContent, validationResult]
  );

  /**
   * Reset validation state
   */
  const reset = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    lastValidatedContentRef.current = "";
    setValidationResult(null);
    setIsValidating(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    isReady,
    validationResult,
    isValidating,
    validate,
    reset,
  };
}
